// Media Review room: each card is one video from Launch Media, played from a signed link that is
// fetched fresh when the card opens (links last an hour). Specs/Media Review Room/PRD.md.
import { useCallback, useEffect, useState } from "react";

import { getSessionToken } from "@/api/auth";
import { mediaLink } from "@/api/media";
import { LaunchPlayer } from "./LaunchPlayer";

import { isShared, type Review, type ReviewItem } from "./api";

type Phase = { state: "loading" } | { state: "ready"; url: string } | { state: "failed" };

// the signed-in link for a Launch Media file
export const signedLink = async (id: string) => (await mediaLink(getSessionToken() || "", id)).url;

// a still of the paused picture as base64 JPEG (no prefix), small enough for the 400 KB limit; null when the
// browser won't let the picture be read (no CORS header on the video), so the comment goes with the time only
export function captureFrame(video: HTMLVideoElement): string | null {
  try {
    if (!video.videoWidth) return null;
    const scale = Math.min(1, 960 / video.videoWidth);
    const c = document.createElement("canvas");
    c.width = Math.round(video.videoWidth * scale); c.height = Math.round(video.videoHeight * scale);
    c.getContext("2d")!.drawImage(video, 0, 0, c.width, c.height);
    const url = c.toDataURL("image/jpeg", 0.7);
    return url.startsWith("data:image/jpeg;base64,") ? url.slice("data:image/jpeg;base64,".length) : null;
  } catch { return null; }
}

// the outcome, what has to be true, and the round: the briefing shown above the videos
export function Briefing({ review }: { review: Pick<Review, "meta"> }) {
  const b = (review.meta as { briefing?: { outcome: string; factors?: string[]; round?: number } }).briefing;
  if (!b) return null;
  return (
    <section className="lr-mbrief">
      <div className="lr-round">Round {b.round || 1}</div>
      <h2>{b.outcome}</h2>
      {!!b.factors?.length && <ul>{b.factors.map((f) => <li key={f}>{f}</li>)}</ul>}
    </section>
  );
}

// what this card proves: the condition id, its story, what to look for, and whether the test passed
export function Condition({ item }: { item: ReviewItem }) {
  if (!item.cid && !item.story && !item.looksLike) return null;
  return (
    <div className="lr-cond" onClick={(e) => e.stopPropagation()}>
      {item.cid && <span className="cid">{item.cid}</span>}
      {item.pass === false && <span className="lr-fail">Test failed</span>}
      {item.story && <p>{item.story}</p>}
      {item.looksLike && <p className="look"><b>What that looks like:</b> {item.looksLike}</p>}
    </div>
  );
}

// one video from Launch Media, played from a signed link fetched fresh. current=false: an earlier round's
// video (shown beside the new one), which the sidebar's frame grab must not pick up.
function Clip({ mediaId, itemId, label, getLink, current = true }: { mediaId?: string; itemId: string; label: string; getLink: (id: string) => Promise<string>; current?: boolean }) {
  const [phase, setPhase] = useState<Phase>({ state: "loading" });
  const [refreshed, setRefreshed] = useState(false);

  const load = useCallback(() => {
    if (!mediaId) return setPhase({ state: "failed" });
    setPhase({ state: "loading" });
    getLink(mediaId).then((url) => setPhase({ state: "ready", url }), () => setPhase({ state: "failed" }));
  }, [mediaId, getLink]);
  useEffect(() => { setRefreshed(false); load(); }, [load]);

  // a link that ran out mid-session stops the video; fetch one new link, then show Try again
  const onError = () => { if (refreshed) return setPhase({ state: "failed" }); setRefreshed(true); load(); };

  if (phase.state === "loading") return <div className="lr-media lr-media-wait">Loading the video…</div>;
  if (phase.state === "failed") {
    return (
      <div className="lr-media lr-media-fail" onClick={(e) => e.stopPropagation()}>
        <p>We couldn't load this video.{isShared() ? " Open this room signed in to watch it." : ""}</p>
        <button type="button" onClick={() => { setRefreshed(false); load(); }}>Try again</button>
      </div>
    );
  }
  return (
    <div className="lr-media" onClick={(e) => e.stopPropagation()}>
      <LaunchPlayer src={phase.url} itemId={itemId} label={label} onError={onError} current={current} />
    </div>
  );
}

// a condition's video; after a new round, "Compare" puts the round before beside this one
export function MediaCard({ item, getLink = signedLink }: { item: ReviewItem; getLink?: (id: string) => Promise<string> }) {
  const [compare, setCompare] = useState(false);
  const before = (item.history || [])[(item.history || []).length - 1];
  const nowRound = before ? before.round + 1 : 1;
  // on a share link there's no account to ask for a link: the owner saved one on each video
  const saved: Record<string, string> = {};
  for (const m of [item.media, ...(item.history || []).map((h) => h.media)]) if (m?.id && (m as { url?: string }).url) saved[m.id] = (m as { url?: string }).url!;
  const link = isShared() ? async (id: string) => { if (!saved[id]) throw new Error("no link"); return saved[id]; } : getLink;
  const now = <Clip mediaId={item.media?.id} itemId={item.id} label={item.media?.name || item.title || "Video"} getLink={link} />;
  if (!before) return now;
  return (
    <div className="lr-rounds" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="lr-cmpbtn" aria-pressed={compare} onClick={() => setCompare(!compare)}>{compare ? "Hide round " + before.round : `Compare with round ${before.round}`}</button>
      {compare ? (
        <div className="lr-compare">
          <figure><figcaption>Round {before.round} (before)</figcaption><Clip mediaId={before.media?.id} itemId={item.id} label={`Round ${before.round}`} getLink={link} current={false} /></figure>
          <figure><figcaption>Round {nowRound} (now)</figcaption>{now}</figure>
        </div>
      ) : now}
    </div>
  );
}
